import Decimal from 'decimal.js'

import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'
import { DevInspectLpUnclaimedRewardsResult, Amount, CoinInfo } from '@kunalabs-io/kai'
import { PhantomTypeArgument } from '@/shared/gen/_framework/reified'

import { useTokenPriceBatch } from 'utils/useTokenPrice'

export const useRewardsFeesTotalValue = (
  positionInfo: PositionInfo,
  unclaimedRewards: DevInspectLpUnclaimedRewardsResult | null,
  vaultDisplayCoinInfo: CoinInfo<PhantomTypeArgument>
) => {
  const configInfo = positionInfo.lastPosition.configInfo

  // create map of all reward amounts
  const rewardAmounts = new Map<
    string,
    { coinInfo: CoinInfo<PhantomTypeArgument>; amount: Amount }
  >()
  const mergeAmount = (coinInfo: CoinInfo<PhantomTypeArgument>, amount: bigint) => {
    const existingAmount = rewardAmounts.get(coinInfo.typeName)?.amount.int ?? 0n
    rewardAmounts.set(coinInfo.typeName, {
      coinInfo,
      amount: coinInfo.newAmount(existingAmount + amount),
    })
  }
  if (unclaimedRewards) {
    mergeAmount(configInfo.X, unclaimedRewards.x.int)
    mergeAmount(configInfo.Y, unclaimedRewards.y.int)
    for (const [coinType, amount] of unclaimedRewards?.rewards || []) {
      mergeAmount(coinType, amount.int)
    }
    for (const [coinType, amount] of unclaimedRewards?.stashed || []) {
      mergeAmount(coinType, amount.int)
    }
  }

  // get all required prices
  const allCoinInfos = Array.from(rewardAmounts.values()).map(v => v.coinInfo)
  const data = useTokenPriceBatch(allCoinInfos, vaultDisplayCoinInfo)

  const allPrices = new Map<string, Decimal>()
  for (const query of data) {
    if (query.isSuccess && query.data) {
      allPrices.set(query.data.X.typeName, query.data.human)
    }
  }

  // calculate total value of all rewards
  let totalSum = new Decimal(0)
  for (const entry of rewardAmounts.values()) {
    const price = allPrices.get(entry.coinInfo.typeName)
    if (!price) {
      continue
    }
    totalSum = totalSum.plus(entry.amount.toDecimal().mul(price))
  }

  const isLoading = data.some(query => query.isLoading) || unclaimedRewards === null
  const isError = data.some(query => query.isError)
  const isSuccess = data.every(query => query.isSuccess)

  return {
    totalSum,
    isLoading,
    isError,
    isSuccess,
  }
}
